import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Dropdown multi-select dengan kotak cari di dalam daftar.
 * options: [{ value, label, sub? }] · value: string[] · onChange(string[])
 */
export function MultiSelect({ options, value, onChange, placeholder = "Semua", allLabel, testid, className = "", width = "w-44", menuWidth = "w-72" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = new Set(value);
  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    return k ? options.filter((o) => `${o.label} ${o.sub || ""}`.toLowerCase().includes(k)) : options;
  }, [options, q]);
  const toggle = (v) => onChange(selected.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length === 0 ? placeholder
    : value.length <= 2 ? value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ")
    : `${value.length} dipilih`;
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2 text-left text-xs transition-colors ${value.length ? "border-sky-500/50 bg-sky-500/10 text-sky-200" : "border-slate-800 bg-slate-900/70 text-slate-300 hover:border-slate-600"} ${width} ${className}`}>
          <span className="flex-1 truncate">{label}</span>
          {value.length > 0 && <span className="rounded bg-sky-500/20 px-1 font-mono text-[10px] text-sky-200" data-testid={`${testid}-count`}>{value.length}</span>}
          {value.length > 0
            ? <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange([]); }} className="rounded p-0.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100" data-testid={`${testid}-clear`}><X className="h-3 w-3" /></span>
            : <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`z-[90] ${menuWidth} border-slate-800 bg-[#0D1524] p-0 text-slate-200 shadow-2xl`} data-testid={`${testid}-menu`}>
        <div className="flex items-center gap-1.5 border-b border-slate-800 px-2 py-1.5">
          <Search className="h-3.5 w-3.5 text-slate-500" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik untuk menyaring…" className="h-6 flex-1 bg-transparent text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none" data-testid={`${testid}-search`} />
          <span className="font-mono text-[10px] text-slate-500">{shown.length}</span>
        </div>
        <div className="flex items-center gap-1 border-b border-slate-800 px-2 py-1 text-[10px]">
          <button type="button" onClick={() => onChange([...new Set([...value, ...shown.map((o) => o.value)])])} className="rounded px-1.5 py-0.5 text-sky-300 hover:bg-sky-500/10" data-testid={`${testid}-all`}>{allLabel || `Pilih semua (${shown.length})`}</button>
          <button type="button" onClick={() => onChange([])} className="rounded px-1.5 py-0.5 text-slate-400 hover:bg-slate-800" data-testid={`${testid}-none`}>Kosongkan</button>
          <span className="ml-auto font-mono text-slate-500">{value.length} dipilih</span>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {shown.length === 0 && <div className="py-4 text-center text-[11px] text-slate-600">Tidak ada pilihan</div>}
          {shown.map((o) => {
            const on = selected.has(o.value);
            return (
              <button type="button" key={o.value} onClick={() => toggle(o.value)}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs ${on ? "bg-sky-500/10 text-sky-100" : "text-slate-300 hover:bg-slate-800/70"}`} data-testid={`${testid}-opt-${o.value}`}>
                <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border ${on ? "border-sky-400 bg-sky-500 text-white" : "border-slate-600"}`}>{on && <Check className="h-2.5 w-2.5" />}</span>
                <span className="truncate font-mono">{o.label}</span>
                {o.sub && <span className="ml-auto truncate text-[10px] text-slate-500">{o.sub}</span>}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Helper filter: kosong = semua lolos */
export const passes = (sel, v) => sel.length === 0 || sel.includes(v ?? "");
